import { MeiliSearch } from 'meilisearch';
import { Logger } from '../logger/Logger';

const meilisearchClient = new MeiliSearch({
  host: process.env.REACT_APP_MEILISEARCH_ENDPOINT ?? '',
  apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY ?? ''
});

const MEILI_POLL_MAX_RETRIES = 10;
const MEILI_POLL_RETRY_DELAY_MS = 1000;

/**
 * Poll a Meilisearch index until the entry with the given id is gone, since the
 * index lags a little behind Strapi deletes. Resolves `true` once the entry is
 * removed, `false` if it is still present after all retries.
 */
const waitForMeiliRemoval = async (
  index: string,
  id: number,
  maxRetries = MEILI_POLL_MAX_RETRIES,
  retryDelayMs = MEILI_POLL_RETRY_DELAY_MS
): Promise<boolean> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await meilisearchClient.index(index).search('', { filter: [`id = ${id}`] });

      if (!result?.hits?.length) {
        return true;
      }
    } catch (error) {
      Logger.error(`Error checking removal of ${id} in Meilisearch index "${index}":`, error);
    }

    if (attempt < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
  }

  return false;
};

/**
 * Poll a Meilisearch index until an upsert has propagated from Strapi, since the
 * index lags a little behind writes. For a creation, resolves `true` once the
 * entry appears; for an update, once the entry's `updatedAt` matches
 * `expectedUpdatedAt`. Resolves `false` if it has not propagated after all retries.
 */
const waitForMeiliUpsert = async (
  index: string,
  id: number,
  {
    expectedUpdatedAt,
    isCreation = false
  }: { expectedUpdatedAt?: string; isCreation?: boolean } = {},
  maxRetries = MEILI_POLL_MAX_RETRIES,
  retryDelayMs = MEILI_POLL_RETRY_DELAY_MS
): Promise<boolean> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await meilisearchClient.index(index).search('', { filter: [`id = ${id}`] });
      const entry = result?.hits?.[0];

      if (entry && (isCreation || entry.updatedAt?.toString() === expectedUpdatedAt)) {
        return true;
      }
    } catch (error) {
      Logger.error(`Error checking upsert of ${id} in Meilisearch index "${index}":`, error);
    }

    if (attempt < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    }
  }

  return false;
};

export { meilisearchClient, waitForMeiliRemoval, waitForMeiliUpsert };
